import { useCallback, useEffect, useState } from 'react'
import { Container } from 'typescript-ioc/es5'
import { AccountService } from '../../../services/account'
import { cancelNamedAxiosRequest } from '../../../config/axios-setup'
import { DashboardDomain } from '../types'

type DomainsState = { status: 'loading' | 'ready' | 'error'; domains: DashboardDomain[] }

const DOMAINS_CANCELLATION_KEY = 'org-dashboard-domains'

/**
 * The organization's domains. The dashboard's range depends on their time zones, so everything else waits for
 * this read. The earlier domains stay available while a retry is in flight and when it fails, so a failed refresh
 * never turns a known organization into one with no domains. A failed read is an error; an empty list is an
 * organization with no domains.
 */
export const useOrgDashboardDomains = (organizationId: number) => {
    const [state, setState] = useState<DomainsState>({ status: 'loading', domains: [] })
    const [attempt, setAttempt] = useState(0)

    useEffect(() => {
        let cancelled = false
        setState((previous) => ({ ...previous, status: 'loading' }))
        Container.get(AccountService)
            .fetchAccountDomainsOrFailure(organizationId, DOMAINS_CANCELLATION_KEY)
            .then((domainDtos) => {
                if (cancelled) return
                if (!domainDtos) {
                    setState((previous) => ({ ...previous, status: 'error' }))
                    return
                }
                setState({
                    status: 'ready',
                    domains: domainDtos.map((domain) => ({
                        id: domain.id,
                        displayName: domain.displayName,
                        hostname: domain.name,
                        currencyCode: domain.currencyCode || undefined,
                        timezone: domain.timezone,
                        ecommerceEnabled: domain.ecommConfig != null,
                    })),
                })
            })
            .catch(() => {
                if (!cancelled) setState((previous) => ({ ...previous, status: 'error' }))
            })
        return () => {
            cancelled = true
            cancelNamedAxiosRequest(DOMAINS_CANCELLATION_KEY)
        }
    }, [organizationId, attempt])

    return { ...state, retry: useCallback(() => setAttempt((count) => count + 1), []) }
}
